import { selector } from 'recoil';
import moment from 'moment';
import { currentMetric } from '../atoms/currentMetric';
import { agentOptions } from '../atoms/agentOptions';
import { startingDate } from '../atoms/startingDate';
import { endingDate } from '../atoms/endingDate';
import { currentTeam } from '../atoms/currentTeam';
import { agentSummary } from './agentSummary';

// Helper function to delay for a given number of milliseconds
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Helper function to retry the state fetching logic
const retryGet = async (get, retries, delayTime) => {
  for (let i = 0; i < retries; i++) {
    try {
      const current = get(currentMetric);
      const options = get(agentOptions);
      const summary = get(agentSummary);
      const team = get(currentTeam);

      if (current && options && summary && team) {
        return { current, options, summary, team };
      }
    } catch (error) {
      // console.log(`Retry ${i + 1}/${retries} failed:`, error);
    }
    await delay(delayTime);
  }
  throw new Error('Failed to retrieve state after retries');
};

export const metrics = selector({
  key: 'metrics',
  get: async ({ get }) => {
    try {
      // Retry state fetching with 5 retries and 500ms delay
      const { current, options, summary, team } = await retryGet(get, 5, 500);

      const result = {};
      const categoryArr = [];
      const seriesArr = [];

      // Deep copy options
      const editable = JSON.parse(JSON.stringify(options));

      // Set team name in title if available
      if (team.id) {
        editable.title.text = `Agent Summary - ${team.name}`;
      }

      // Set the yAxis title
      editable.yAxis.title.text = current.title;

      // Ensure stats is an array before processing
      const editableStats = JSON.parse(JSON.stringify(summary));
      if (Array.isArray(editableStats?.stats)) {
        editableStats.stats.sort((a, b) => b[current.key] - a[current.key]);
        editableStats.stats.forEach((i) => {
          if (i[current.key] && i.email) {
            categoryArr.push(i.email);
            seriesArr.push(i[current.key]);
          }
        });
      }

      // Ensure xAxis and series are initialized properly
      editable.xAxis = editable.xAxis || {};
      editable.xAxis.categories = editable.xAxis.categories || [];

      editable.series = editable.series || [{ data: [] }];

      // Assign categories and series data
      editable.xAxis.categories = categoryArr;
      editable.series[0].data = seriesArr;

      result.options = editable;
      result.current = current;

      // Handle dates
      let start = get(startingDate);
      let end = get(endingDate);
      if (start._i && end._i) {
        start = moment(start._i).format('YYYY-MM-DD');
        end = moment(end._i).format('YYYY-MM-DD');
      }

      return result;
    } catch (error) {
      // console.error('Error in metrics selector:', error);
      return {}; // Optionally return a fallback empty result
    }
  },
  set: ({ set }, newValue) => set(currentMetric, newValue),
});
